/**
 * ★ Ponte com a extensão Chrome (app.md §8) — o ponto mais sensível da migração.
 *
 * Três formas de comunicação no legado:
 *   1. window.postMessage (getSync/getLocal/setLocal/deleteLocal/module/stop) — content.js
 *   2. chrome.runtime.sendMessage (sendRuntime)
 *   3. iframe chrome-extension://<id>/html/popup.html
 *
 * PROBLEMA legado: um único resolver global (`promiseResolver`) — chamadas concorrentes
 * se sobrescrevem ([application.js:980-1029]).
 *
 * SOLUÇÃO: cada request leva um `_rid` (crypto.randomUUID). Se a extensão ecoar o `_rid`
 * (protocolo novo), casamos a resposta na hora. Se NÃO ecoar (extensão legada), caímos num
 * fallback por TIPO: uma resposta sem `_rid` só pode resolver uma request cujo tipo esperado
 * corresponda. Payload cru só é aceito quando todas as pendências compatíveis são do mesmo tipo.
 * Isso preserva extensões antigas sem permitir que concorrência entregue sync a capabilities.
 *
 * REGRA (§15.6): nenhum componente usa window.postMessage direto — só este módulo.
 */
import { DEBUG } from '../config/env';
import {
  isCaptureBatchEnvelope,
  isCaptureBatchMessage,
  type CaptureAckResult,
  type CaptureBatch,
  type CaptureAckMessage,
} from '../types/captureProtocol';

const BRIDGE_TIMEOUT = 8000;
const CAPABILITIES_TIMEOUT = 1200;
const SYNC_CONFIRM_READ_TIMEOUT = 600;
const SYNC_CONFIRM_POLL_INTERVAL = 40;

interface Pending {
  rid: string;
  type: string;
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/** Requests aguardando resposta; a ordem só desempata respostas legadas do mesmo tipo. */
const pendingQueue: Pending[] = [];
let listenerAttached = false;

const RESPONSE_TYPE_BY_REQUEST: Readonly<Record<string, string>> = Object.freeze({
  getSync: 'sync',
  getLocal: 'local',
  getCapabilities: 'capabilities',
  checkExtensionVersion: 'extensionVersionResponse',
});

/** Assinantes de broadcasts da extensão (count/module_change/stop etc.). */
type BroadcastHandler = (data: Record<string, unknown>) => void;
const subscribers = new Set<BroadcastHandler>();

export type CaptureBatchHandler = (
  batch: CaptureBatch,
  context: { ackId: string; runId?: string },
) => CaptureAckResult | Promise<CaptureAckResult>;
let captureHandler: CaptureBatchHandler | null = null;

/**
 * Assina broadcasts vindos da extensão (mensagens que NÃO são respostas a um request nosso).
 * Ex.: contadores de captura, `stop`, troca de módulo. Retorna função de cancelamento.
 * REGRA (§15.6): componentes usam isto — nunca window.addEventListener('message') direto.
 */
export function onExtensionMessage(handler: BroadcastHandler): () => void {
  attachListener();
  subscribers.add(handler);
  return () => subscribers.delete(handler);
}

/**
 * Canal único de lotes, baseado na POC
 * [archive/02-local-first/frontend-app2-local-first/src/lib/extensionBridge.ts:68-100].
 * O contrato atual valida envelope e payload separadamente para responder `invalid_batch`.
 */
export function onCaptureBatch(handler: CaptureBatchHandler): () => void {
  attachListener();
  captureHandler = handler;
  return () => {
    if (captureHandler === handler) captureHandler = null;
  };
}

function postCaptureAck(ackId: string, result: CaptureAckResult, runId?: string): void {
  const message: CaptureAckMessage = {
    __captureAck: ackId,
    ...(runId ? { runId } : {}),
    result,
  };
  try {
    window.postMessage(message, '*');
  } catch (error) {
    if (DEBUG) console.warn('[bridge] captureAck falhou', error);
  }
}

function attachListener(): void {
  if (listenerAttached) return;
  listenerAttached = true;
  window.addEventListener('message', onMessage);
}

function onMessage(e: MessageEvent): void {
  // Aceitamos mensagens da própria janela (content script injeta no mesmo contexto).
  // Em ambiente real o source é `window`; em jsdom/testes é `null`. Rejeitamos apenas
  // mensagens vindas de OUTRO frame (source definido e diferente de window) — ex.: o iframe.
  if (e.source && e.source !== window) return;
  const data = e.data as Record<string, unknown> | null;
  if (!data || typeof data !== 'object') return;

  // O painel só emite ACKs de captura; nunca os consome. Ignorá-los pelo campo
  // do próprio contrato evita que o postMessage volte como broadcast ou resposta.
  if ('__captureAck' in data) return;

  // Ignora mensagens que são claramente REQUESTS nossas (evita auto-resposta).
  if (data['__bridgeRequest'] === true) return;

  if (data['__captureBatch'] === true) {
    if (!isCaptureBatchEnvelope(data) || !captureHandler) return;
    const rawBatch = data.batch;
    const runId =
      rawBatch &&
      typeof rawBatch === 'object' &&
      !Array.isArray(rawBatch) &&
      typeof (rawBatch as Record<string, unknown>)['runId'] === 'string'
        ? ((rawBatch as Record<string, unknown>)['runId'] as string)
        : undefined;
    if (!isCaptureBatchMessage(data)) {
      postCaptureAck(data.ackId, { ok: false, error: 'invalid_batch' }, runId);
      return;
    }

    void Promise.resolve(captureHandler(data.batch, { ackId: data.ackId, runId: data.batch.runId }))
      .then((result) => postCaptureAck(data.ackId, result, data.batch.runId))
      .catch(() =>
        postCaptureAck(data.ackId, { ok: false, error: 'internal_error' }, data.batch.runId),
      );
    return;
  }

  // Entrega o broadcast aos assinantes (capture counts/stop/module). Eles filtram o que importa.
  if (subscribers.size > 0) {
    for (const handler of subscribers) {
      try {
        handler(data);
      } catch {
        /* um assinante com erro não deve derrubar os demais */
      }
    }
  }

  const rid = typeof data['_rid'] === 'string' ? (data['_rid'] as string) : undefined;

  // Protocolo novo: casa pelo _rid.
  if (rid) {
    const idx = pendingQueue.findIndex((p) => p.rid === rid);
    if (idx >= 0) {
      const [p] = pendingQueue.splice(idx, 1);
      if (p) settle(p, data);
      return;
    }
  }

  // Protocolo legado (sem _rid): casa pelo tipo esperado. Ordem FIFO só desempata requests
  // equivalentes; nunca permite que `{type:'sync'}` resolva `getCapabilities`.
  if (!rid && looksLikeResponse(data) && pendingQueue.length > 0) {
    const idx = legacyPendingIndex(data);
    const [p] = idx >= 0 ? pendingQueue.splice(idx, 1) : [];
    if (p) settle(p, data);
  }
}

function legacyPendingIndex(data: Record<string, unknown>): number {
  const responseType = typeof data['type'] === 'string' ? data['type'] : undefined;
  if (responseType) {
    return pendingQueue.findIndex((pending) => RESPONSE_TYPE_BY_REQUEST[pending.type] === responseType);
  }

  // Extensões anteriores ao envelope `{type,result}` devolviam storage cru. Sem `_rid` nem
  // `type`, só há correlação segura quando todas as candidatas pedem o mesmo storage; respostas
  // do mesmo tipo são snapshots intercambiáveis e continuam FIFO.
  const candidates = pendingQueue
    .map((pending, index) => ({ pending, index }))
    .filter(({ pending }) => pending.type === 'getSync' || pending.type === 'getLocal');
  if (candidates.length === 0) return -1;
  const expectedType = candidates[0]?.pending.type;
  return candidates.every(({ pending }) => pending.type === expectedType) ? (candidates[0]?.index ?? -1) : -1;
}

/**
 * Heurística para distinguir uma RESPOSTA legada de um broadcast de UI da própria página
 * (ex.: {type:'stop'} / {count, module_change}). Respostas de sync/local trazem dados úteis.
 */
function looksLikeResponse(data: Record<string, unknown>): boolean {
  if ('captureQueueDropped' in data) return false;
  // Sinal da extensão para o registro de uso: broadcast, nunca resposta de getSync/getLocal.
  if ('__activitySignal' in data) return false;
  const t = data['type'];
  if (t === 'stop' || t === 'module' || t === 'module_change' || t === 'getSync' || t === 'getLocal') {
    return false;
  }
  return true;
}

function settle(p: Pending, data: Record<string, unknown>): void {
  clearTimeout(p.timer);
  // Remove metadados internos antes de entregar ao chamador.
  const clean = { ...data };
  delete clean['_rid'];
  delete clean['__bridgeRequest'];
  p.resolve(clean);
}

/** Envia um request e aguarda resposta correlacionada (ou fallback legado por tipo). */
export function bridgeRequest<T = unknown>(
  type: string,
  payload: Record<string, unknown> = {},
  timeoutMs = BRIDGE_TIMEOUT,
): Promise<T> {
  attachListener();
  const rid =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `rid_${Date.now()}_${Math.random().toString(36).slice(2)}`;

  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      const idx = pendingQueue.findIndex((p) => p.rid === rid);
      if (idx >= 0) pendingQueue.splice(idx, 1);
      reject(new Error(`extension bridge timeout (${type})`));
    }, timeoutMs);

    pendingQueue.push({ rid, type, resolve: resolve as (v: unknown) => void, reject, timer });

    try {
      window.postMessage({ type, _rid: rid, __bridgeRequest: true, ...payload }, '*');
      if (DEBUG) console.debug('[bridge] →', type, rid);
    } catch (err) {
      clearTimeout(timer);
      const idx = pendingQueue.findIndex((p) => p.rid === rid);
      if (idx >= 0) pendingQueue.splice(idx, 1);
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}

/** Fire-and-forget (setLocal/deleteLocal/module/stop). Não espera resposta. */
export function bridgeSend(type: string, payload: Record<string, unknown> = {}): void {
  attachListener();
  try {
    window.postMessage({ type, __bridgeRequest: true, ...payload }, '*');
    if (DEBUG) console.debug('[bridge] → (send)', type);
  } catch (err) {
    if (DEBUG) console.warn('[bridge] send failed', type, err);
  }
}

/* ---------- Helpers tipados dos comandos usados pelo painel ---------- */

/**
 * A extensão real responde getSync/getLocal com `{ type: 'sync'|'local', result: <storage> }`
 * (content.js) — o dado real fica em `.result`. Desembrulha, como o legado
 * (`promiseResolver(event.data.result)` [application.js:15298/15303]). Se a resposta não vier
 * embrulhada (ex.: em testes/mocks), devolve como está.
 */
function unwrapStorage<T>(res: Record<string, unknown>): T {
  if (res && typeof res === 'object' && 'result' in res && (res['type'] === 'sync' || res['type'] === 'local')) {
    return res['result'] as T;
  }
  return res as T;
}

export async function getSync<T = Record<string, unknown>>(timeoutMs = BRIDGE_TIMEOUT): Promise<T> {
  return unwrapStorage<T>(await bridgeRequest<Record<string, unknown>>('getSync', {}, timeoutMs));
}

export async function getLocal<T = Record<string, unknown>>(): Promise<T> {
  return unwrapStorage<T>(await bridgeRequest<Record<string, unknown>>('getLocal'));
}

/**
 * Consulta as capabilities do código vivo da extensão.
 *
 * Isto deliberadamente NÃO usa getSync/chrome.storage.sync: uma declaração sincronizada
 * sobreviveria a downgrade e poderia fazer uma extensão antiga parecer compatível.
 */
export function getExtensionCapabilities(timeoutMs = CAPABILITIES_TIMEOUT): Promise<Record<string, unknown>> {
  return bridgeRequest<Record<string, unknown>>('getCapabilities', {}, timeoutMs);
}

export interface ExtensionVersionResponse {
  type: 'extensionVersionResponse';
  version: string;
}

/**
 * Prova de presença suportada pela extensão 7.0.5.8 publicada
 * (`extension_antiga/js/content.js:49-51`). A versão é somente informativa: suporte ao
 * local-first continua dependendo exclusivamente da declaração viva de capabilities.
 */
export async function getExtensionVersion(
  timeoutMs = CAPABILITIES_TIMEOUT,
): Promise<ExtensionVersionResponse> {
  const response = await bridgeRequest<Record<string, unknown>>(
    'checkExtensionVersion',
    {},
    timeoutMs,
  );
  if (
    response['type'] !== 'extensionVersionResponse' ||
    typeof response['version'] !== 'string' ||
    response['version'].trim().length === 0
  ) {
    throw new Error('invalid extension version response');
  }
  return {
    type: 'extensionVersionResponse',
    version: response['version'],
  };
}

/**
 * Grava no chrome.storage.SYNC via content.js (`{type:'setSync', ...}`). Porta de
 * `setDataSync` [application.js:1029]. É por aqui que o painel controla a CAPTURA:
 * `setSync({ play: true })` inicia / `{ play: false }` para — a extensão (aba de busca,
 * background e popup) reage à mudança de `play` via chrome.storage.onChanged.
 */
export function setSync(data: Record<string, unknown>): void {
  bridgeSend('setSync', data);
}

/**
 * Falha ao comprovar que o snapshot enviado já está visível no `chrome.storage.sync` vivo.
 * A navegação não pode sair nesse caso: a aba nova inicializaria com estado antigo e ficaria
 * aberta sem raspar, enquanto o painel já diria que a captura está rodando.
 */
export class ExtensionSyncCommitError extends Error {
  constructor() {
    super('A extensão não confirmou o estado de início da captura.');
    this.name = 'ExtensionSyncCommitError';
  }
}

function sameStoredValue(actual: unknown, expected: unknown): boolean {
  if (Object.is(actual, expected)) return true;
  if (actual === null || expected === null) return false;
  if (typeof actual !== 'object' || typeof expected !== 'object') return false;
  try {
    return JSON.stringify(actual) === JSON.stringify(expected);
  } catch {
    return false;
  }
}

function containsSyncPatch(
  snapshot: Record<string, unknown>,
  expected: Record<string, unknown>,
): boolean {
  return Object.entries(expected).every(([key, value]) => sameStoredValue(snapshot[key], value));
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

/**
 * Publica um snapshot e só resolve quando a própria extensão o devolver por `getSync`.
 *
 * O `setSync` legado é fire-and-forget: o content script inicia `chrome.storage.sync.set` e
 * retorna antes do callback. Abrir/navegar a aba nesse intervalo permite que `init()` leia
 * `play:false` da execução anterior. A confirmação por leitura funciona também com a extensão
 * 8.1 já publicada, sem acoplar o deploy do painel a um ACK de protocolo ainda inexistente.
 */
export async function setSyncConfirmed(
  data: Record<string, unknown>,
  timeoutMs = BRIDGE_TIMEOUT,
): Promise<void> {
  setSync(data);
  const deadline = Date.now() + Math.max(1, timeoutMs);

  while (Date.now() < deadline) {
    const remaining = deadline - Date.now();
    try {
      const snapshot = await getSync<Record<string, unknown>>(
        Math.max(1, Math.min(SYNC_CONFIRM_READ_TIMEOUT, remaining)),
      );
      if (containsSyncPatch(snapshot, data)) return;
    } catch {
      // A extensão pode estar entre navegações. Até o prazo, uma nova leitura é segura.
    }

    const waitFor = Math.min(SYNC_CONFIRM_POLL_INTERVAL, deadline - Date.now());
    if (waitFor > 0) await wait(waitFor);
  }

  throw new ExtensionSyncCommitError();
}

export function setLocal(data: Record<string, unknown>): void {
  bridgeSend('setLocal', data);
}

export function deleteLocal(name: string): void {
  bridgeSend('deleteLocal', { name });
}

export function setModule(module: string): void {
  bridgeSend('module', { module });
}

export function stopCapture(): void {
  bridgeSend('stop');
}

/**
 * Envia um comando "runtime" à extensão (relay do content script → chrome.runtime).
 * Porta de `sendRuntime` [application.js:1029]. Ex.: iniciar scraping com { url, active:true }.
 */
export function sendRuntime(data: Record<string, unknown>): void {
  bridgeSend('runtime', data);
}

/**
 * Detecta a extensão por dois sinais vivos independentes. `checkExtensionVersion` responde
 * imediatamente na 7.0.5.8 e evita que uma leitura lenta/indisponível do storage gere o CTA;
 * `getSync` preserva compatibilidade com builds ainda mais antigos.
 * Resolve assim que qualquer sinal válido responder, sem esperar o outro timeout.
 * Usado para (a) o banner de aviso e (b) o gate dos recursos que exigem a extensão.
 */
export async function pingExtension(timeoutMs = 1200): Promise<boolean> {
  attachListener();
  try {
    await Promise.any([
      getExtensionVersion(timeoutMs),
      getSync(timeoutMs).then((result) => {
        if (result == null) throw new Error('invalid extension sync response');
        return result;
      }),
    ]);
    return true;
  } catch {
    return false;
  }
}

/** Para testes: limpa a fila, os assinantes e os timers. */
export function __resetBridge(): void {
  for (const p of pendingQueue) clearTimeout(p.timer);
  pendingQueue.length = 0;
  subscribers.clear();
  captureHandler = null;
}
